import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Clock, ArrowLeft, Shield } from 'lucide-react';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import LeadForm from '../components/LeadForm';
import { api } from '../lib/api';

export default function ClinicProfile() {
  const { id } = useParams();
  const [clinic, setClinic] = useState(null);
  const [allClinics, setAllClinics] = useState([]);

  useEffect(() => {
    api.get(`/clinics/${id}`).then((r) => setClinic(r.data.clinic));
    api.get('/clinics/public').then((r) => setAllClinics(r.data.clinics || []));
  }, [id]);

  if (!clinic) {
    return <Layout><div className="max-w-5xl mx-auto px-4 py-24 text-center text-[#64748B]">Laster…</div></Layout>;
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    name: clinic.name,
    address: { '@type': 'PostalAddress', addressLocality: clinic.city, addressCountry: 'NO' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: String(clinic.rating), reviewCount: String(clinic.reviews) },
  };

  return (
    <Layout>
      <Seo
        title={`${clinic.name} — ${clinic.location} | Specialists.no`}
        description={`Book konsultasjon med ${clinic.name} i ${clinic.city}. ${clinic.rating} stjerner. ${clinic.treatments.join(', ')}. Gratis henvendelse.`}
        canonical={`https://specialists.no/klinikker/${clinic.id}`}
        jsonLd={jsonLd}
      />

      <section className="bg-[#0A1F44] text-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-white/50 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-white">Hjem</Link> / <Link to="/klinikker" className="hover:text-white">Klinikker</Link> / <span className="text-white">{clinic.name}</span>
          </nav>
          <div className="flex items-start flex-wrap gap-5 mb-4">
            {clinic.featured && (
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#3B82F6] text-white px-3 py-1 rounded-full">
                <Shield size={12} /> Partner
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider bg-white/10 text-white px-3 py-1 rounded-full">
              <Clock size={12} className="text-[#3B82F6]" /> {clinic.waitTime}
            </span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight mb-4">{clinic.name}</h1>
          <div className="flex items-center gap-4 text-white/80">
            <div className="flex items-center gap-1">
              <Star size={16} fill="#3B82F6" className="text-[#3B82F6]" />
              <span className="font-semibold text-white text-lg">{clinic.rating}</span>
              <span className="text-sm text-white/60">({clinic.reviews} anmeldelser)</span>
            </div>
            <span className="text-white/40">•</span>
            <span>{clinic.location}</span>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-10">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-4">Behandlinger</h2>
            <div className="flex flex-wrap gap-2 mb-10">
              {clinic.treatments.map((t) => (<span key={t} className="bg-white border border-[#0A1F44]/10 text-[#0A1F44] px-4 py-2 rounded-full text-sm">{t}</span>))}
            </div>

            <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-4">Om klinikken</h2>
            <p className="text-[#334155] leading-relaxed mb-6">
              {clinic.name} er en godkjent partnerklinikk på Specialists.no med {clinic.rating} stjerner basert på {clinic.reviews} pasientanmeldelser. Klinikken tilbyr {clinic.treatments.length} behandlinger innen {clinic.categories.join(' og ')} i {clinic.location}.
            </p>
            <p className="text-xs text-[#64748B] italic">Rating basert på Google og Trustpilot</p>

            <Link to="/klinikker" className="inline-flex items-center gap-2 mt-8 text-[#0A1F44] font-semibold hover:text-[#3B82F6]">
              <ArrowLeft size={16} /> Alle klinikker
            </Link>
          </div>

          <div className="lg:sticky lg:top-28 h-fit">
            <LeadForm
              initialClinic={clinic.id}
              initialCategory={clinic.categories[0]}
              initialCity={clinic.city}
              clinics={allClinics}
              compact
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}
